import { useCallback, useEffect, useMemo, useState } from 'react';

import { type InternalRefetchQueriesInclude } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { Alert, useTheme } from '@mui/material';
import {
  Activity,
  Banknote,
  BookOpen,
  Copy,
  GalleryVerticalEnd,
  House,
  Megaphone,
  Tag,
  Trash2,
  Users,
} from 'lucide-react';
import {
  Navigate,
  Route,
  Routes,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import type { Actions } from '@modules/_shared/components/actions-menu/ActionsMenu';
import { GET_BUYER_LEAD_OFFER } from '@modules/_shared/components/buyer-lead/BuyerLeadOfferCard';
import { Drawer } from '@modules/_shared/components/drawer/Drawer';
import {
  type DrawerTab,
  generateTab,
} from '@modules/_shared/components/drawer/DrawerTopBar';
import RASnackbar from '@modules/_shared/components/RASnackbar';
import { TopbarTitle } from '@modules/_shared/components/topbar/Topbar';
import { useNavigateWithSearchParams } from '@modules/_shared/hooks/useNavigateWithSearchParams';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { formatAddress } from '@modules/_shared/utils/formatting';
import { canEditListing } from '@modules/_shared/utils/listingPermissions';
import { GET_DASHBOARD_LISTINGS } from '@modules/dashboard/dashboardQueries';
import { ListingActivity } from '@modules/listings/ListingActivity/ListingActivity';
import { ListingBrochure } from '@modules/listings/ListingBrochure';
import { GET_LISTING_BUYERS_DASHBOARD } from '@modules/listings/listingBuyersDashboardQueries';
import { ListingBuyersLead } from '@modules/listings/ListingBuyersLead';
import { ListingDuplicateDialog } from '@modules/listings/ListingDuplicateDialog';
import { ListingMarketing } from '@modules/listings/ListingMarketing';
import { ListingMedia } from '@modules/listings/ListingMedia/ListingMedia';
import {
  GET_LISTING_OFFERS_QUERY,
  ListingOffers,
} from '@modules/listings/ListingOffers';
import { ListingOverview } from '@modules/listings/ListingOverview';
import { initialListingsListQuery } from '@modules/listings/Listings';
import { ListingTransactions } from '@modules/listings/ListingTransactions';
import { DELETE_LOT, GET_LOT } from '@modules/listings/lotsQueries';
import { OfferDrawer } from '@modules/offers/OfferDrawer';
import { useLocale } from '@shared/hooks/locale';

const RedirectToMedia = ({ sub }: { sub: 'images' | 'documents' }) => {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const rest = sub === 'images' ? params['*'] ?? '' : '';

  return (
    <Navigate
      replace
      to={{
        pathname: `../media/${sub}${rest ? `/${rest}` : ''}`,
        search: searchParams.toString(),
      }}
    />
  );
};

export const ListingDrawer = ({
  basePath = '/listings',
}: {
  basePath?: string;
}) => {
  const { t } = useLocale();
  const navigate = useNavigateWithSearchParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { me } = useAppData();
  const params = useParams();
  const { lotId, developmentId } = params;
  const { breakpoints } = useTheme();
  const [openDuplicateDialog, setOpenDuplicateDialog] = useState(false);
  const [displayDuplicateSnackbar, setDisplayDuplicateSnackbar] =
    useState(false);

  const selectedTab = params['*']?.split('/')[0] ?? '';

  const { data, error } = useQuery(GET_LOT, {
    variables: { id: lotId ?? '' },
    skip: lotId == null,
  });

  const [deleteLot] = useMutation(DELETE_LOT, {
    refetchQueries: [GET_DASHBOARD_LISTINGS],
  });

  const handleClose = useCallback(() => navigate('../'), [navigate]);

  const tabsDef: [string, string, React.ReactElement?, boolean?][] = [
    [t('Listing'), '', <House key="listing" size={18} />],
    [t('Activity'), 'activity', <Activity key="activity" size={18} />],
    [t('Media'), 'media', <GalleryVerticalEnd key="media" size={18} />],
    [t('Brochure'), 'brochure', <BookOpen key="brochure" size={18} />],
    [t('Marketing'), 'marketing', <Megaphone key="marketing" size={18} />],
    [t('Buyers'), 'buyers', <Users key="buyers" size={18} />],
    [t('Offers'), 'offers', <Tag key="offers" size={18} />],
    [t('Transaction'), 'transaction', <Banknote key="transaction" size={18} />],
  ];

  const tabs: DrawerTab[] = tabsDef.map(([label, value, icon, disabled]) =>
    generateTab([searchParams.toString(), label, value, icon, disabled]),
  );

  const title = useMemo(() => {
    if (data?.lots_by_pk == null) {
      return t('Listing');
    }

    return (
      data.lots_by_pk.title || formatAddress(data.lots_by_pk.property, ', ')
    );
  }, [data, t]);

  const lot = data?.lots_by_pk;
  const canEdit = useMemo(() => canEditListing(me, lot), [lot, me]);

  const createOfferOpen = searchParams.get('create_offer') != null;
  const createOfferBuyerId = searchParams.get('buyerId');

  const closeCreateOffer = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('create_offer');
    next.delete('buyerId');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (createOfferOpen && lot != null && !canEdit) {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          next.delete('create_offer');
          next.delete('buyerId');
          return next;
        },
        { replace: true },
      );
    }
  }, [createOfferOpen, lot, canEdit, setSearchParams]);

  const offerRefetchQueries = useMemo<InternalRefetchQueriesInclude>(() => {
    if (lotId == null) {
      return [];
    }
    const queries: InternalRefetchQueriesInclude = [
      { query: GET_LISTING_BUYERS_DASHBOARD, variables: { id: lotId } },
      { query: GET_LISTING_OFFERS_QUERY, variables: { listingId: lotId } },
    ];
    if (createOfferBuyerId != null) {
      queries.push({
        query: GET_BUYER_LEAD_OFFER,
        variables: { buyerId: createOfferBuyerId, lotId },
      });
    }
    return queries;
  }, [lotId, createOfferBuyerId]);

  const memoizedOverview = useMemo(
    () => <ListingOverview onAccessLost={handleClose} />,
    [handleClose],
  );

  const drawerActions = useMemo(() => {
    const actions = [
      me?.is_admin
        ? {
            label: t('Delete'),
            disabled: false,
            onClick: () => {
              if (
                window.confirm(
                  t('Are you sure you want to delete this listing?'),
                ) &&
                lotId != null
              ) {
                // Delete listing and remove from cache
                deleteLot({
                  variables: { id: lotId },
                  update: cache => {
                    cache.evict({ id: `lots:${lotId}` });
                  },
                }).then(() => {
                  // Close drawer
                  handleClose();
                });
              }
            },
            icon: <Trash2 size={16} />,
          }
        : null,
      canEdit
        ? {
            label: t('Duplicate'),
            disabled: false,
            onClick: () => setOpenDuplicateDialog(true),
            icon: <Copy size={16} />,
          }
        : null,
    ].filter(Boolean) as Actions[];

    return actions.length > 0 ? actions : undefined;
  }, [canEdit, me, lotId, t, deleteLot, handleClose]);

  const topbarTitle = (
    <TopbarTitle titleOverride={title}>
      <></>
    </TopbarTitle>
  );

  if (lotId == null) {
    return (
      <>
        {topbarTitle}
        <Alert severity="error" sx={{ m: 2 }}>
          {t('Listing not found')}
        </Alert>
      </>
    );
  }

  return (
    <>
      {topbarTitle}
      <Drawer
        onClose={handleClose}
        title={title}
        actions={drawerActions}
        currentTab={selectedTab}
        maxWidth={developmentId != null ? breakpoints.values.lg : undefined}
        tabs={tabs}
      >
        {error != null ? (
          <Alert severity="error">{JSON.stringify(error)}</Alert>
        ) : (
          <>
            <ListingDuplicateDialog
              listingId={lotId}
              open={openDuplicateDialog}
              onClose={() => setOpenDuplicateDialog(false)}
              onSuccess={lotId => {
                setOpenDuplicateDialog(false);
                setDisplayDuplicateSnackbar(true);
                navigate(
                  developmentId != null
                    ? `/developments/${developmentId}/lots/${lotId}`
                    : `${basePath}/${lotId}`,
                );
              }}
              refetchQueries={[
                initialListingsListQuery(
                  searchParams,
                  developmentId != null,
                  developmentId,
                ),
              ]}
            />
            {displayDuplicateSnackbar && (
              <RASnackbar
                onClose={() => setDisplayDuplicateSnackbar(false)}
                message={t('Listing duplicated successfully!')}
                severity="success"
              />
            )}
            <Routes>
              <Route index element={memoizedOverview} />
              <Route
                path="activity"
                element={<ListingActivity lotId={lotId} readOnly={!canEdit} />}
              />
              <Route
                path="buyers/*"
                element={<ListingBuyersLead readOnly={!canEdit} />}
              />
              <Route
                path="brochure"
                element={<ListingBrochure key={lotId} readOnly={!canEdit} />}
              />
              <Route
                path="media/*"
                element={
                  <ListingMedia
                    lotId={lotId}
                    propertyId={lot?.property?.id}
                    readOnly={!canEdit}
                  />
                }
              />
              <Route
                path="images/*"
                element={<RedirectToMedia sub="images" />}
              />
              <Route
                path="documents"
                element={<RedirectToMedia sub="documents" />}
              />
              <Route
                path="property"
                element={
                  <Navigate
                    replace
                    to={{ pathname: '..', search: searchParams.toString() }}
                  />
                }
              />
              <Route
                path="marketing/*"
                element={<ListingMarketing readOnly={!canEdit} />}
              />
              <Route
                path="offers/*"
                element={<ListingOffers readOnly={!canEdit} />}
              />
              <Route
                path="transaction/*"
                element={<ListingTransactions readOnly={!canEdit} />}
              />
            </Routes>
          </>
        )}
      </Drawer>
      {createOfferOpen && canEdit && (
        <OfferDrawer
          onClose={closeCreateOffer}
          onCreate={closeCreateOffer}
          refetchQueries={offerRefetchQueries}
        />
      )}
    </>
  );
};

export default ListingDrawer;
